import React, { useEffect, useMemo, useState } from "react";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { deleteCountry, getCountries } from "../services/countryService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import { useAppContext } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";

const CountryManagementPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const load = async () => {
    try {
      setLoading(true);
      const res = await getCountries();
      setList(Array.isArray(res) ? res : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => {
      const name = String(c.countryName || "").toLowerCase();
      const iso2 = String(c.isoCode2 || "").toLowerCase();
      const iso3 = String(c.isoCode3 || "").toLowerCase();
      return name.includes(q) || iso2.includes(q) || iso3.includes(q);
    });
  }, [list, searchTerm]);

  const getRowId = (c) => c?.countryId ?? c?.country_id ?? c?.id ?? null;

  const handleDelete = async (countryId, countryName) => {
    if (!countryId) return;
    const label = countryName || `ID ${countryId}`;
    const shouldDelete = window.confirm(
      `Are you sure you want to delete "${label}"? This action cannot be undone.`,
    );
    if (!shouldDelete) return;

    try {
      setDeletingId(countryId);
      await deleteCountry(countryId);
      toast.success(`Country "${label}" deleted successfully.`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed to delete country");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="page-container country-page">
      {!roleChecked ?
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      : !canAccess ?
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      : <>
          <div className="page-header">
            <div className="header-content">
              <div>
                <h2 className="page-title">Country Management</h2>
                <p className="page-subtitle">Manage countries and ISO codes</p>
              </div>
              <div className="roles-toolbar">
                <div className="roles-search">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    className="secondary-btn sm"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </button>
                </div>
                <button
                  className="primary-btn"
                  onClick={() => setActiveSection("country-create")}
                >
                  + Create Country
                </button>
              </div>
            </div>
          </div>

          <div className="page-content">
            {loading ?
              <div className="no-results">Loading countries...</div>
            : <div className="kpi-table-container">
                {rows.length === 0 ? (
                  <div className="no-results">No countries found.</div>
                ) : (
                  <table className="kpi-table">
                    <colgroup>
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "56%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "7%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Country Name</th>
                        <th>ISO Code 2</th>
                        <th>ISO Code 3</th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => {
                        const rowId = getRowId(c);
                        return (
                          <tr key={rowId ?? `${c.countryName || "country"}-${c.isoCode2 || ""}`}>
                            <td>{rowId ?? <span className="empty-text">-</span>}</td>
                            <td>{c.countryName || <span className="empty-text">-</span>}</td>
                            <td>{c.isoCode2 || <span className="empty-text">-</span>}</td>
                            <td>{c.isoCode3 || <span className="empty-text">-</span>}</td>
                            <td className="actions-cell">
                              <div
                                className="actions-menu-wrapper"
                                style={{ position: "relative", display: "inline-block" }}
                              >
                                <button
                                  className="action-menu-btn"
                                  aria-haspopup="true"
                                  aria-expanded={activeDropdown === rowId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeDropdown === rowId) {
                                      setActiveDropdown(null);
                                    } else {
                                      setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                                      setActiveDropdown(rowId);
                                    }
                                  }}
                                  disabled={rowId == null}
                                >
                                  Actions ▼
                                </button>
                                {activeDropdown === rowId && (
                                  <div
                                    className={`action-dropdown-menu${
                                      dropdownDirection === "up" ? " dropdown-up" : ""
                                    }`}
                                    role="menu"
                                  >
                                    <button
                                      className="action-menu-item"
                                      role="menuitem"
                                      disabled={rowId == null}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(null);
                                        if (rowId == null) return;
                                        try {
                                          localStorage.setItem("activeCountryId", String(rowId));
                                        } catch {}
                                        setActiveSection("country-edit", { countryId: rowId });
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="action-menu-item danger"
                                      role="menuitem"
                                      disabled={rowId == null || deletingId === rowId}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(null);
                                        if (rowId != null) {
                                          handleDelete(rowId, c.countryName);
                                        }
                                      }}
                                    >
                                      {deletingId === rowId ? "Deleting..." : "Delete"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            }
          </div>
        </>
      }
    </div>
  );
};

export default CountryManagementPage;

